import React, { useState, useEffect } from "react";
import {
  CreditCardIcon,
  UserGroupIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { BillingCycle, PlanType, BillingInfo } from "../../types/billing";
import { billingService } from "../../services/billingService";
import { SubscriptionChangePreview } from "./SubscriptionChangePreview";

interface SubscriptionManagerProps {
  organizationId: string;
  currentPlan: PlanType;
  currentBillingCycle: BillingCycle;
  currentEditors: number;
  actualEditorCount?: number; // Actual editors/admins in the organization
  maxEditors?: number;
  isManualBilling: boolean;
  hasActiveSubscription: boolean;
  scheduledChanges?: BillingInfo["scheduled_changes"];
  onUpdate: () => void;
  onScheduleDowngrade?: (
    newPlan: PlanType,
    editorCount?: number,
    newBillingCycle?: BillingCycle,
    force?: boolean,
  ) => Promise<void>;
  onScheduleBillingCycleChange?: (
    newBillingCycle: BillingCycle,
    force?: boolean,
  ) => Promise<void>;
  isScheduledForCancellation?: boolean;
}

export const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  organizationId,
  currentPlan,
  currentBillingCycle,
  currentEditors,
  actualEditorCount,
  maxEditors,
  isManualBilling,
  hasActiveSubscription,
  scheduledChanges,
  onUpdate,
  onScheduleDowngrade,
  onScheduleBillingCycleChange,
  isScheduledForCancellation,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(currentPlan);
  const [selectedBillingCycle, setSelectedBillingCycle] =
    useState<BillingCycle>(currentBillingCycle);
  const [selectedEditors, setSelectedEditors] = useState(currentEditors);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when props change
  useEffect(() => {
    setSelectedPlan(currentPlan);
    setSelectedBillingCycle(currentBillingCycle);
    setSelectedEditors(currentEditors);
  }, [currentPlan, currentBillingCycle, currentEditors]);

  const plans: Array<{
    type: PlanType;
    name: string;
    description: string;
    features: string[];
  }> = [
    {
      type: "starter",
      name: "Starter",
      description: "Perfect for small teams",
      features: [
        "Up to 5 users",
        "3 propositions",
        "3 segments",
        "AI research",
      ],
    },
    {
      type: "professional",
      name: "Professional",
      description: "For growing organizations",
      features: [
        "Unlimited users",
        "Unlimited propositions",
        "Unlimited segments",
        "Advanced analytics",
      ],
    },
  ];

  // Get max editors for a plan type
  const getMaxEditorsForPlan = (planType: PlanType): number | undefined => {
    if (planType === "starter") return 5;
    if (planType === "professional") return undefined; // unlimited
    return 1; // free plan
  };

  const handlePlanChange = (newPlan: PlanType) => {
    setSelectedPlan(newPlan);

    // Adjust editor count if it exceeds the new plan's limit
    const maxForNewPlan = getMaxEditorsForPlan(newPlan);
    if (maxForNewPlan && selectedEditors > maxForNewPlan) {
      setSelectedEditors(maxForNewPlan);
    }
  };

  const hasChanges = () => {
    return (
      selectedPlan !== currentPlan ||
      selectedBillingCycle !== currentBillingCycle ||
      selectedEditors !== currentEditors
    );
  };

  const handlePreviewChanges = () => {
    if (hasChanges()) {
      setShowPreview(true);
    }
  };

  const handleConfirmChanges = async () => {
    setIsLoading(true);
    setShowPreview(false);

    try {
      // Check what type of changes we have
      const planChanged = selectedPlan !== currentPlan;
      const cycleChanged = selectedBillingCycle !== currentBillingCycle;
      const editorsChanged = selectedEditors !== currentEditors;

      // Handle new subscription from free plan
      if (currentPlan === "free" && selectedPlan !== "free") {
        const session = await billingService.createSubscription(
          organizationId,
          selectedPlan,
          selectedBillingCycle,
          selectedEditors,
        );
        window.location.href = session.checkout_url;
        return;
      }

      // Handle editor-only changes (immediate)
      if (!planChanged && !cycleChanged && editorsChanged) {
        await billingService.updateEditorCount(organizationId, selectedEditors);
        setError(null); // Clear any previous errors
        onUpdate();
        return;
      }

      // Handle billing cycle changes (with or without editor changes, but same plan)
      if (!planChanged && cycleChanged) {
        // When changing billing cycle, it might also include editor changes
        // The backend change-billing-cycle endpoint will handle both
        const result = await billingService.changeBillingCycle(
          organizationId,
          selectedBillingCycle,
          false, // don't force initially
        );

        // Check if there's a conflict
        if ("status" in result && result.status === "conflict") {
          // Use the dedicated billing cycle change handler if available
          if (onScheduleBillingCycleChange) {
            // This will handle the conflict dialog with force option
            await onScheduleBillingCycleChange(selectedBillingCycle, false);
          } else {
            // Fallback: Show detailed error with the conflict information
            const existingChange = result.existing_change;
            setError(
              `A change is already scheduled for ${existingChange?.change_date || "end of period"}. ` +
                `Please cancel the existing change before scheduling a new billing cycle change.`,
            );
          }
          // Don't update - keep the form open so user can see the error/dialog
        } else {
          // If editors also changed, update them separately
          if (editorsChanged) {
            try {
              await billingService.updateEditorCount(
                organizationId,
                selectedEditors,
              );
            } catch (editorError: any) {
              console.error("Failed to update editor count:", editorError);
              // Don't fail the whole operation if editor update fails
              // The billing cycle change already succeeded
            }
          }
          setError(null); // Clear any previous errors
          onUpdate();
        }
        return;
      }

      // Handle plan changes (with or without other changes)
      const isUpgrade = isPlanUpgrade();
      const isBillingDowngrade =
        currentBillingCycle === "annual" && selectedBillingCycle === "monthly";

      // Determine if this should be scheduled or immediate
      // Rule: If ANY component requires scheduling, the entire change is scheduled
      const shouldSchedule = !isUpgrade || isBillingDowngrade;

      if (planChanged && shouldSchedule) {
        // Scheduled change (downgrade or mixed change with billing downgrade)
        // Examples: Pro → Starter, Starter Annual → Pro Monthly
        if (onScheduleDowngrade) {
          // Use the provided handler which includes conflict handling
          await onScheduleDowngrade(
            selectedPlan,
            selectedEditors,
            selectedBillingCycle,
            false, // don't force initially
          );
        } else {
          // Fallback to direct service call with basic conflict handling
          const result = await billingService.scheduleDowngrade(
            organizationId,
            selectedPlan,
            selectedEditors,
            selectedBillingCycle,
          );

          // Check if there's a conflict
          if ("status" in result && result.status === "conflict") {
            // Show error about existing scheduled change
            setError(
              "A plan change is already scheduled. Please cancel the existing change before scheduling a new one, or use the billing portal to manage complex changes.",
            );
            // Don't update - keep the form open so user can see the error
          } else {
            // Success - downgrade scheduled
            setError(null);
            onUpdate();
          }
        }
      } else if (planChanged && !shouldSchedule) {
        // Immediate upgrade (plan upgrade without billing downgrade)
        // Examples: Starter → Pro, Pro Monthly → Starter Annual
        await billingService.upgradeSubscription(
          organizationId,
          selectedPlan,
          selectedBillingCycle,
          selectedEditors,
        );
        setError(null); // Clear any previous errors
        onUpdate();
      } else {
        // This shouldn't happen with current logic
        // All cases should be covered above
        console.error("Unexpected subscription change scenario");
        setError(
          "Unexpected subscription change scenario. Please contact support.",
        );
      }
    } catch (error: any) {
      console.error("Failed to update subscription:", error);
      // Extract error message from the API response
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Failed to update subscription. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isPlanUpgrade = () => {
    const planHierarchy: Record<string, number> = {
      free: 0,
      starter: 1,
      professional: 2,
      enterprise: 3,
    };
    return planHierarchy[selectedPlan] > planHierarchy[currentPlan];
  };

  const isBillingUpgrade = () => {
    return (
      currentBillingCycle === "monthly" && selectedBillingCycle === "annual"
    );
  };

  if (isManualBilling) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <div className="flex">
          <CreditCardIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-900">
              Manually Managed Billing
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Your subscription is managed directly with our sales team. Contact
              your account manager for changes.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show locked state when subscription is scheduled for cancellation
  if (isScheduledForCancellation) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex">
          <svg
            className="h-5 w-5 text-amber-400 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-900">
              Subscription Scheduled for Cancellation
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              Your subscription is scheduled to cancel at the end of the current
              billing period. To make changes to your subscription, please
              reactivate it first using the button below.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (scheduledChanges && scheduledChanges.managed_in_stripe) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <div className="flex">
          <svg
            className="h-5 w-5 text-amber-400 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
              clipRule="evenodd"
            />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-900">
              Complex Subscription Changes in Progress
            </h3>
            <p className="mt-1 text-sm text-amber-700">
              You have scheduled changes that involve multiple modifications (
              {scheduledChanges.message}). These complex changes must be managed
              through the Stripe Portal. Please use the &quot;Manage Billing &
              Payment Methods&quot; button below to modify or cancel your
              scheduled changes before making new ones.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Manage Subscription
        </h3>

        {/* Error Alert */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
            <button
              className="absolute top-0 bottom-0 right-0 px-4 py-3"
              onClick={() => setError(null)}
            >
              <span className="text-red-500 text-xl">&times;</span>
            </button>
          </div>
        )}

        {/* Plan Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <CreditCardIcon className="inline h-4 w-4 mr-1" />
            Plan Type
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plans.map((plan) => (
              <label
                key={plan.type}
                className={`relative rounded-lg border p-4 cursor-pointer hover:border-gray-400 ${
                  selectedPlan === plan.type
                    ? "border-blue-500 ring-2 ring-blue-500"
                    : "border-gray-200"
                }`}
              >
                <input
                  type="radio"
                  name="plan"
                  value={plan.type}
                  checked={selectedPlan === plan.type}
                  onChange={(e) => handlePlanChange(e.target.value as PlanType)}
                  className="sr-only"
                />
                <div>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">
                        {plan.name}
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {plan.description}
                      </p>
                    </div>
                    {selectedPlan === plan.type && (
                      <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                    )}
                  </div>
                  <ul className="mt-2 space-y-1">
                    {plan.features.map((feature, idx) => (
                      <li
                        key={idx}
                        className="text-xs text-gray-600 flex items-start"
                      >
                        <span className="mr-1">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Billing Cycle */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <CalendarDaysIcon className="inline h-4 w-4 mr-1" />
            Billing Cycle
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label
              className={`relative rounded-lg border p-4 cursor-pointer hover:border-gray-400 ${
                selectedBillingCycle === "monthly"
                  ? "border-blue-500 ring-2 ring-blue-500"
                  : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="billing"
                value="monthly"
                checked={selectedBillingCycle === "monthly"}
                onChange={(e) =>
                  setSelectedBillingCycle(e.target.value as BillingCycle)
                }
                className="sr-only"
              />
              <div>
                <h4 className="text-sm font-medium text-gray-900">Monthly</h4>
                <p className="mt-1 text-xs text-gray-500">Pay month-to-month</p>
              </div>
            </label>
            <label
              className={`relative rounded-lg border p-4 cursor-pointer hover:border-gray-400 ${
                selectedBillingCycle === "annual"
                  ? "border-blue-500 ring-2 ring-blue-500"
                  : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                name="billing"
                value="annual"
                checked={selectedBillingCycle === "annual"}
                onChange={(e) =>
                  setSelectedBillingCycle(e.target.value as BillingCycle)
                }
                className="sr-only"
              />
              <div>
                <h4 className="text-sm font-medium text-gray-900">Annual</h4>
                <p className="mt-1 text-xs text-gray-500">
                  Save with yearly billing
                </p>
                <p className="mt-1 text-xs font-medium text-green-600">
                  Save 17%
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Editor Seats */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <UserGroupIcon className="inline h-4 w-4 mr-1" />
            Editor Seats
          </label>
          <div className="flex items-center space-x-4">
            <div className="relative group">
              <button
                onClick={() => {
                  if (
                    actualEditorCount &&
                    selectedEditors > actualEditorCount
                  ) {
                    setSelectedEditors(Math.max(1, selectedEditors - 1));
                  }
                }}
                disabled={
                  selectedEditors <= 1 ||
                  (actualEditorCount !== undefined &&
                    selectedEditors <= actualEditorCount)
                }
                className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 12H4"
                  />
                </svg>
              </button>
              {/* Tooltip for disabled state */}
              {actualEditorCount &&
                selectedEditors <= actualEditorCount &&
                selectedEditors > 1 && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                    <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
                      <div className="font-medium mb-1">
                        Cannot reduce editor seats
                      </div>
                      <div>
                        You have {actualEditorCount} active{" "}
                        {actualEditorCount === 1 ? "editor" : "editors"} in your
                        organization.
                      </div>
                      <div className="mt-1">
                        Remove editor permissions from team members first.
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <div className="border-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-900">
                {selectedEditors}
              </div>
              <div className="text-xs text-gray-500">
                {selectedEditors === 1 ? "editor" : "editors"}
              </div>
            </div>
            {/* Warning when actual editors exceed selected count */}
            {actualEditorCount && actualEditorCount > selectedEditors && (
              <div className="ml-2">
                <div className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                  <svg
                    className="w-3 h-3 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {actualEditorCount} active
                </div>
              </div>
            )}
            <button
              onClick={() => setSelectedEditors(selectedEditors + 1)}
              disabled={(() => {
                const maxForPlan = getMaxEditorsForPlan(selectedPlan);
                return (
                  maxForPlan !== undefined && selectedEditors >= maxForPlan
                );
              })()}
              className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            {(() => {
              const maxForPlan = getMaxEditorsForPlan(selectedPlan);
              if (maxForPlan) {
                const planName =
                  selectedPlan.charAt(0).toUpperCase() + selectedPlan.slice(1);
                return `Maximum ${maxForPlan} editors on ${planName} plan`;
              }
              return "Each editor can create and modify content";
            })()}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              setSelectedPlan(currentPlan);
              setSelectedBillingCycle(currentBillingCycle);
              setSelectedEditors(currentEditors);
            }}
            disabled={!hasChanges()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={handlePreviewChanges}
            disabled={!hasChanges() || isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Processing..." : "Review Changes"}
          </button>
        </div>
      </div>

      {/* Preview Dialog */}
      <SubscriptionChangePreview
        open={showPreview}
        onClose={() => setShowPreview(false)}
        onConfirm={handleConfirmChanges}
        organizationId={organizationId}
        planType={selectedPlan}
        billingCycle={selectedBillingCycle}
        editorCount={selectedEditors}
        loading={isLoading}
      />
    </div>
  );
};
